<template>
  <!-- Segmented category tabs for the brand picker. Scrolls horizontally; edge
  fades + arrows only appear while tabs overflow that side. -->
  <div class="relative rounded-lg bg-neutral-10 border border-border-normal">
    <div
      ref="tabs"
      class="flex items-center gap-0.5 p-1 overflow-x-auto scrollbar-hide"
      @scroll.passive="updateOverflow"
    >
      <button
        v-for="category in categories"
        :key="category.id"
        type="button"
        class="tab flex-shrink-0 px-2.5 py-1.5 rounded-md whitespace-nowrap border focus:outline-none"
        :class="value === category.id
          ? 'bg-white text-text-loud shadow-sm border-border-normal'
          : 'text-text-subdued border-transparent hover:text-text-muted hover:bg-neutral-25'"
        @click="$emit('input', category.id)"
      >
        <BaseText
          type="label"
          size="sm"
        >
          {{ category.label }}
        </BaseText>
      </button>
    </div>

    <div
      class="fade fade--left"
      :class="{ 'opacity-0': atStart }"
      aria-hidden="true"
    />
    <div
      class="fade fade--right"
      :class="{ 'opacity-0': atEnd }"
      aria-hidden="true"
    />
    <button
      type="button"
      aria-label="Scroll categories left"
      class="arrow arrow--left"
      :class="{ 'arrow--hidden': atStart }"
      :tabindex="atStart ? -1 : 0"
      @click="scrollBy(-1)"
    >
      <CarouselChevronIcon
        :width="16"
        :height="16"
      />
    </button>
    <button
      type="button"
      aria-label="Scroll categories right"
      class="arrow arrow--right"
      :class="{ 'arrow--hidden': atEnd }"
      :tabindex="atEnd ? -1 : 0"
      @click="scrollBy(1)"
    >
      <CarouselChevronIcon
        class="transform rotate-180"
        :width="16"
        :height="16"
      />
    </button>
  </div>
</template>

<script>
import CarouselChevronIcon from '../globals/Icons/CarouselChevronIcon.vue'

const SCROLL_STEP_PX = 200
// Slack for sub-pixel scroll positions
const EDGE_TOLERANCE_PX = 4

export default {
  name: 'SetupBrandCategoryTabs',
  components: { CarouselChevronIcon },
  props: {
    // [{ id, label }]
    categories: {
      type: Array,
      required: true
    },
    // Selected category id (v-model)
    value: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      // Start hidden so fades/arrows only appear once overflow is measured
      atStart: true,
      atEnd: true
    }
  },
  mounted () {
    // Re-measure whenever the strip resizes (modal opening, fonts, window resize)
    this.resizeObserver = new ResizeObserver(this.updateOverflow)
    this.resizeObserver.observe(this.$refs.tabs)
  },
  beforeDestroy () {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    updateOverflow () {
      const el = this.$refs.tabs
      if (!el) return
      this.atStart = el.scrollLeft <= EDGE_TOLERANCE_PX
      this.atEnd = el.scrollWidth - el.clientWidth - el.scrollLeft <= EDGE_TOLERANCE_PX
    },
    scrollBy (direction) {
      this.$refs.tabs.scrollBy({ left: direction * SCROLL_STEP_PX, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.tab {
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}
.fade {
  position: absolute;
  top: 1px;
  bottom: 1px;
  width: 48px;
  pointer-events: none;
  transition: opacity 150ms ease;
}
.fade--left {
  left: 1px;
  border-radius: 7px 0 0 7px;
  background: linear-gradient(270deg, rgba(248, 250, 251, 0) 0%, theme('colors.neutral.10') 60%);
}
.fade--right {
  right: 1px;
  border-radius: 0 7px 7px 0;
  background: linear-gradient(90deg, rgba(248, 250, 251, 0) 0%, theme('colors.neutral.10') 60%);
}
.arrow {
  position: absolute;
  top: 50%;
  width: 24px;
  height: 24px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid theme('colors.neutral.50');
  background-color: theme('colors.white');
  color: theme('colors.neutral.500');
  box-shadow: 0 1px 2px rgba(6, 7, 16, 0.06);
  transition: opacity 150ms ease, background-color 150ms ease, color 150ms ease;
}
.arrow:hover {
  background-color: theme('colors.neutral.25');
  color: theme('colors.neutral.600');
}
.arrow:focus {
  outline: none;
}
.arrow--left {
  left: 6px;
}
.arrow--right {
  right: 6px;
}
.arrow--hidden {
  opacity: 0;
  pointer-events: none;
}
</style>
